import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { ENDPOINTS } from '../api/endpoints';

/**
 * Hook to fetch referral statistics for the logged-in user
 * @param {string} userAddress - User's wallet address (optional, will skip fetch if not provided)
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Whether to fetch immediately on mount (default: true)
 * @returns {Object} - { stats, loading, error, refetch }
 */
export const useReferralStats = (userAddress, options = {}) => {
  const { immediate = true } = options;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    if (!userAddress) {
      setStats(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(
        `${ENDPOINTS.REFERRAL.STATS}?address=${encodeURIComponent(userAddress)}`
      );
      setStats(response?.stats || null);
    } catch (err) {
      console.error('[useReferralStats] Error fetching stats:', err);
      setError(err?.message || 'Failed to fetch referral stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (immediate && userAddress) {
      fetchStats();
    }
  }, [userAddress, immediate]);

  const refetch = () => {
    if (userAddress) {
      fetchStats();
    }
  };

  return {
    stats,
    loading,
    error,
    refetch,
  };
};

export default useReferralStats;
